import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { ImageCarousel } from '@/components/ImageCarousel';
import { MapErrorBoundary } from '@/components/MapErrorBoundary';
import {
  Bed, Bath, Wifi, CheckCircle2, MapPin, Car, Waves, Dumbbell,
  Sparkles, Coffee, Wind, PawPrint, Cigarette, Zap, Phone, Mail,
  MessageCircle, ArrowLeft, Home as HomeIcon, Tv, Refrigerator,
  Microwave, ShieldCheck, Sun, Mountain, Trees, Flame, Lock,
  Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const approximatePosition = (listing) => {
  const seed = (listing.id || '').split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const jitterLat = (((seed % 100) / 100) - 0.5) * 0.006;
  const jitterLng = ((((seed * 7) % 100) / 100) - 0.5) * 0.006;
  return {
    lat: (listing.lat || -33.925) + jitterLat,
    lng: (listing.lng || 18.410) + jitterLng,
  };
};

const priceFromListing = (listing) => {
  const map = listing.monthly_prices || {};
  const values = Object.values(map).filter((v) => typeof v === 'number' && v > 0);
  if (values.length > 0) return Math.min(...values);
  return listing.monthly_price;
};

const photoUrl = (photo) => {
  if (!photo) return null;
  const path = photo.storage_path;
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API}/files/${path}`;
};

const AmenityItem = ({ icon: Icon, label, enabled }) => {
  if (!enabled) return null;
  return (
    <div className="flex items-center space-x-3 py-3">
      <Icon className="w-5 h-5 text-ink" strokeWidth={1.5} />
      <span className="text-ink">{label}</span>
    </div>
  );
};

export const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    fetchListing();
    if (user?.role === 'guest') checkFavourite();
  }, [id, user]);

  const checkFavourite = async () => {
    try {
      const { data } = await axios.get(`${API}/favourites`, { withCredentials: true });
      setIsFavourite(data.some((l) => l.id === id));
    } catch (e) { /* ignore */ }
  };

  const handleToggleFavourite = async () => {
    if (!user) {
      setShowSignupPrompt(true);
      return;
    }
    try {
      if (isFavourite) {
        await axios.delete(`${API}/favourites/${id}`, { withCredentials: true });
        setIsFavourite(false);
        toast.success('Removed from favourites');
      } else {
        await axios.post(`${API}/favourites/${id}`, {}, { withCredentials: true });
        setIsFavourite(true);
        toast.success('Added to favourites');
      }
    } catch (e) {
      toast.error('Failed to update favourites');
    }
  };

  const fetchListing = async () => {
    try {
      const { data } = await axios.get(`${API}/listings/${id}`);
      setListing(data);
    } catch (error) {
      toast.error('Property not found');
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = () => {
    if (!user) {
      setShowSignupPrompt(true);
      return;
    }
    setShowContact(true);
    // Track that the guest contacted this listing
    axios.post(`${API}/favourites/track-contact/${id}`, {}, { withCredentials: true }).catch(() => {});
  };

  const handleWhatsAppClick = () => {
    if (!user) {
      setShowSignupPrompt(true);
      return;
    }
    if (!listing.contact_whatsapp) {
      toast.error('WhatsApp not provided for this listing');
      return;
    }
    const cleanNumber = listing.contact_whatsapp.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(`Hi! I saw your listing "${listing.title}" on Cape Town Monthly and would love to know more.`);
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-ink"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="text-center">
          <p className="text-muted mb-4 font-serif italic">Property not found</p>
          <Button onClick={() => navigate('/listings')} className="btn-editorial btn-solid">
            Browse Listings
          </Button>
        </div>
      </div>
    );
  }

  const photos = listing.photos || [];
  const fromPrice = priceFromListing(listing);
  const monthlyPricesMap = listing.monthly_prices || {};
  const mapPosition = approximatePosition(listing);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <button
          onClick={() => navigate('/listings')}
          className="flex items-center text-sm text-muted hover:text-ink mb-6 caps"
          data-testid="property-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={2} />
          Back to listings
        </button>

        {/* Title */}
        <div className="mb-8 pb-6 border-b" style={{ borderBottomColor: 'var(--line)' }}>
          <div className="flex items-center gap-2 mb-3">
            {listing.verification_status === 'approved' && (
              <div className="inline-flex items-center gap-1.5 bg-white rounded-md px-2.5 py-1.5 text-[11px] tracking-[0.1em] uppercase font-bold" style={{ color: 'var(--sea)', border: '1px solid var(--line-2)' }}>
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                Checked
              </div>
            )}
            <div className="text-[13.5px] text-muted">{listing.neighbourhood}, Cape Town</div>
          </div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-display text-[36px] md:text-[48px] tracking-[-0.03em] text-ink leading-[1.06]" data-testid="property-title">
              {listing.title}
            </h1>
            <button
              onClick={handleToggleFavourite}
              className="p-2 rounded-full hover:bg-paper-2 transition-colors"
              data-testid="favourite-btn"
              aria-label="Save to favourites"
            >
              <Heart
                className={`w-6 h-6 ${isFavourite ? 'fill-ink text-ink' : 'text-ink'}`}
                strokeWidth={1.5}
              />
            </button>
          </div>
        </div>

        {/* Image layout: mobile carousel, desktop = 1 rectangle + 2 stacked squares */}
        <div className="mb-8 md:mb-12 h-[280px] sm:h-[400px] md:h-[500px]">
          {photos.length === 0 ? (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <HomeIcon className="w-16 h-16 text-gray-400" />
            </div>
          ) : (
            <>
              {/* Mobile: single-image carousel */}
              <div className="md:hidden w-full h-full relative" data-testid="hero-mobile-carousel">
                <ImageCarousel photos={photos} alt={listing.title} showCounter />
              </div>
              {/* Desktop: Airbnb-style grid */}
              <div className="hidden md:block h-full">
                {photos.length === 1 ? (
                  <div className="w-full h-full relative">
                    <ImageCarousel photos={photos} alt={listing.title} showCounter />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 h-full">
                    {/* Main rectangle - takes 2 cols */}
                    <div
                      className="col-span-2 row-span-2 relative cursor-pointer group"
                      onClick={() => setGalleryOpen(true)}
                      data-testid="hero-image-main"
                    >
                      <img
                        src={photoUrl(photos[0])}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors" />
                    </div>
                    {/* Two stacked squares on the right */}
                    <div
                      className="relative cursor-pointer group overflow-hidden"
                      onClick={() => setGalleryOpen(true)}
                      data-testid="hero-image-2"
                    >
                      {photos[1] ? (
                        <img
                          src={photoUrl(photos[1])}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : <div className="w-full h-full bg-gray-200" />}
                      <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors" />
                    </div>
                    <div
                      className="relative cursor-pointer group overflow-hidden"
                      onClick={() => setGalleryOpen(true)}
                      data-testid="hero-image-3"
                    >
                      {photos[2] ? (
                        <img
                          src={photoUrl(photos[2])}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : <div className="w-full h-full bg-gray-200" />}
                      <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors" />
                      {photos.length > 3 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setGalleryOpen(true); }}
                          className="absolute bottom-3 right-3 bg-paper text-ink px-3 py-1.5 caps text-[10px] shadow-md hover:bg-white transition-colors"
                          data-testid="show-all-photos-btn"
                        >
                          Show all {photos.length} photos
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview */}
            <div className="border-b pb-8" style={{ borderBottomColor: 'var(--line)' }}>
              <h2 className="font-serif text-3xl mb-4">
                {listing.property_type} in {listing.neighbourhood}
              </h2>
              <div className="flex flex-wrap gap-6 text-ink">
                <div className="flex items-center gap-2">
                  <Bed className="w-5 h-5" strokeWidth={1.5} />
                  <span>{listing.bedrooms} bedroom{listing.bedrooms !== 1 && 's'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bath className="w-5 h-5" strokeWidth={1.5} />
                  <span>{listing.bathrooms} bathroom{listing.bathrooms !== 1 && 's'}</span>
                </div>
                {listing.square_metres && (
                  <div className="flex items-center gap-2">
                    <HomeIcon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{listing.square_metres}m²</span>
                  </div>
                )}
                {listing.wifi_speed && (
                  <div className="flex items-center gap-2">
                    <Wifi className="w-5 h-5" strokeWidth={1.5} />
                    <span>{listing.wifi_speed}Mbps WiFi</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="border-b pb-8" style={{ borderBottomColor: 'var(--line)' }}>
              <h2 className="font-serif text-3xl mb-4">About</h2>
              <p className="text-ink leading-relaxed whitespace-pre-wrap" data-testid="property-description">
                {listing.description}
              </p>
            </div>

            {/* Amenities */}
            <div className="border-b pb-8" style={{ borderBottomColor: 'var(--line)' }}>
              <h2 className="font-serif text-3xl mb-4">Amenities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 divide-y" style={{ borderColor: 'var(--line)' }}>
                <AmenityItem icon={Wifi} label={`Fast WiFi (${listing.wifi_speed}Mbps)`} enabled={!!listing.wifi_speed} />
                <AmenityItem icon={Coffee} label="Workspace" enabled={listing.workspace} />
                <AmenityItem icon={Car} label="Parking" enabled={listing.parking} />
                <AmenityItem icon={Waves} label="Pool" enabled={listing.pool} />
                <AmenityItem icon={Dumbbell} label="Gym" enabled={listing.gym} />
                <AmenityItem icon={Sparkles} label="Laundry" enabled={listing.laundry} />
                <AmenityItem icon={HomeIcon} label="Full kitchen" enabled={listing.kitchen} />
                <AmenityItem icon={Wind} label="Air conditioning" enabled={listing.air_conditioning} />
                <AmenityItem icon={Flame} label="Heating" enabled={listing.heating} />
                <AmenityItem icon={PawPrint} label="Pet friendly" enabled={listing.pet_friendly} />
                <AmenityItem icon={Cigarette} label="Smoking allowed" enabled={listing.smoking} />
                <AmenityItem icon={Zap} label="Utilities included" enabled={listing.utilities_included} />
                <AmenityItem icon={Refrigerator} label="Refrigerator" enabled={listing.refrigerator} />
                <AmenityItem icon={Microwave} label="Microwave" enabled={listing.microwave} />
                <AmenityItem icon={HomeIcon} label="Oven" enabled={listing.oven} />
                <AmenityItem icon={HomeIcon} label="Stove" enabled={listing.stove} />
                <AmenityItem icon={HomeIcon} label="Dishwasher" enabled={listing.dishwasher} />
                <AmenityItem icon={Coffee} label="Coffee maker" enabled={listing.coffee_maker} />
                <AmenityItem icon={Tv} label="Smart TV" enabled={listing.smart_tv} />
                <AmenityItem icon={Tv} label="TV" enabled={listing.tv && !listing.smart_tv} />
                <AmenityItem icon={Sparkles} label="Hair dryer" enabled={listing.hair_dryer} />
                <AmenityItem icon={Sparkles} label="Iron" enabled={listing.iron} />
                <AmenityItem icon={ShieldCheck} label="Smoke alarm" enabled={listing.smoke_alarm} />
                <AmenityItem icon={ShieldCheck} label="Security" enabled={listing.security} />
                <AmenityItem icon={Lock} label="Self check-in" enabled={listing.self_check_in} />
                <AmenityItem icon={Waves} label="Sea view" enabled={listing.sea_view} />
                <AmenityItem icon={Mountain} label="Mountain view" enabled={listing.mountain_view} />
                <AmenityItem icon={Sun} label="City view" enabled={listing.city_view} />
                <AmenityItem icon={Trees} label="Garden view" enabled={listing.garden_view} />
                <AmenityItem icon={Waves} label="Beachfront" enabled={listing.beachfront} />
                <AmenityItem icon={Trees} label="Outdoor space / Balcony" enabled={listing.outdoor_space || listing.balcony} />
                <AmenityItem icon={Flame} label="BBQ" enabled={listing.bbq} />
                <AmenityItem icon={Waves} label="Hot tub" enabled={listing.hot_tub} />
                <AmenityItem icon={Zap} label="EV charger" enabled={listing.ev_charger} />
              </div>
            </div>

            {/* Availability & pricing */}
            <div className="border-b pb-8" style={{ borderBottomColor: 'var(--line)' }}>
              <h2 className="font-serif text-3xl mb-4">Availability & monthly pricing</h2>
              <p className="text-muted mb-4 text-sm">Available for stays of {listing.minimum_stay}+ nights{listing.maximum_stay && `, up to ${listing.maximum_stay} nights`}.</p>
              {listing.available_months && listing.available_months.length > 0 ? (
                <div className="space-y-2 max-w-md">
                  {[...listing.available_months].sort().map((m) => {
                    const price = monthlyPricesMap[m];
                    const label = new Date(m + '-01').toLocaleDateString('en', { month: 'long', year: 'numeric' });
                    const formattedPrice = Number(price ?? listing.monthly_price).toLocaleString('en-ZA').replace(/,/g, ' ');
                    return (
                      <div
                        key={m}
                        className="flex justify-between items-baseline border-b border-line pb-2"
                        data-testid={`availability-${m}`}
                      >
                        <span className="text-ink">{label}:</span>
                        <span className="font-serif text-ink font-semibold">
                          R{formattedPrice}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-muted italic font-serif text-sm">Contact owner for availability</span>
              )}
            </div>

            {/* Location + map */}
            <div className="border-b pb-8" style={{ borderBottomColor: 'var(--line)' }}>
              <h2 className="font-serif text-3xl mb-4">Location</h2>
              <p className="text-ink mb-2">
                <MapPin className="w-4 h-4 inline mr-1" strokeWidth={1.5} />
                {listing.neighbourhood}, Cape Town
              </p>
              <p className="text-sm text-muted font-serif italic mb-4">Marker shows approximate location — exact address is shared after contact.</p>
              {GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY' && (
                <div className="h-[320px] border border-line" data-testid="property-map">
                  <MapErrorBoundary>
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <Map
                        style={{ width: '100%', height: '100%' }}
                        defaultCenter={mapPosition}
                        defaultZoom={14}
                        mapId="cape-town-monthly-map"
                        gestureHandling="cooperative"
                        mapTypeControl={false}
                        streetViewControl={false}
                        fullscreenControl={false}
                      >
                        <AdvancedMarker position={mapPosition}>
                          <div className="bg-ink text-paper px-3 py-1.5 caps text-[10px] shadow-lg">
                            {listing.neighbourhood}
                          </div>
                        </AdvancedMarker>
                      </Map>
                    </APIProvider>
                  </MapErrorBoundary>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Sticky Contact Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 border p-6 bg-paper" style={{ borderColor: 'var(--line-strong)' }}>
              <div className="mb-4">
                <div className="caps text-muted text-[10px] mb-1">From</div>
                <div className="font-serif text-4xl text-ink" data-testid="property-price-from">R{Number(fromPrice).toLocaleString()}</div>
                <div className="text-muted">per month</div>
              </div>

              <div className="border-t border-b py-4 my-4 space-y-2" style={{ borderColor: 'var(--line)' }}>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Minimum stay</span>
                  <span className="font-semibold text-ink">{listing.minimum_stay} nights</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">Property type</span>
                  <span className="font-semibold text-ink">
                    {listing.place_type === 'private_room' ? 'Private room' : listing.property_type}
                  </span>
                </div>
              </div>

              {listing.contact_whatsapp && listing.contact_whatsapp_visible !== false && (
                <Button
                  onClick={handleWhatsAppClick}
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-6 mb-3 rounded-none label-caps text-xs"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" strokeWidth={2} />
                  Message on WhatsApp
                </Button>
              )}

              <Button
                onClick={handleContactClick}
                className="w-full btn-editorial btn-solid"
                data-testid="show-contact-btn"
              >
                Show Contact Details
              </Button>

              <p className="text-xs text-muted text-center mt-4 font-serif italic">
                No booking fees · Deal directly with owner
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal - only for logged in users */}
      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent data-testid="contact-modal" className="bg-paper border-gray-400 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Contact <em className="italic">{listing.host_name}</em>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {listing.contact_phone && listing.contact_phone_visible !== false && (
              <div className="flex items-center space-x-3 p-4 border" style={{ borderColor: 'var(--line)' }}>
                <Phone className="w-5 h-5 text-ink" strokeWidth={1.5} />
                <div>
                  <div className="caps text-muted text-[10px]">Phone</div>
                  <a href={`tel:${listing.contact_phone}`} className="font-semibold hover:underline text-ink">
                    {listing.contact_phone}
                  </a>
                </div>
              </div>
            )}
            {listing.contact_email && listing.contact_email_visible !== false && (
              <div className="flex items-center space-x-3 p-4 border" style={{ borderColor: 'var(--line)' }}>
                <Mail className="w-5 h-5 text-ink" strokeWidth={1.5} />
                <div>
                  <div className="caps text-muted text-[10px]">Email</div>
                  <a href={`mailto:${listing.contact_email}?subject=Re: ${listing.title}`} className="font-semibold hover:underline text-ink">
                    {listing.contact_email}
                  </a>
                </div>
              </div>
            )}
            {listing.contact_whatsapp && listing.contact_whatsapp_visible !== false && (
              <div className="flex items-center space-x-3 p-4 border" style={{ borderColor: 'var(--line)' }}>
                <MessageCircle className="w-5 h-5 text-ink" strokeWidth={1.5} />
                <div>
                  <div className="caps text-muted text-[10px]">WhatsApp</div>
                  <button
                    onClick={handleWhatsAppClick}
                    className="font-semibold hover:underline text-ink"
                  >
                    {listing.contact_whatsapp}
                  </button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted font-serif italic pt-2">
              Reach out directly. Cape Town Monthly does not process rental payments or agreements.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Gallery */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent data-testid="gallery-modal" className="bg-paper border-gray-400 rounded-none max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Photo gallery</DialogTitle>
          </DialogHeader>
          <div className="h-[70vh] relative">
            <ImageCarousel photos={photos} alt={listing.title} showCounter />
          </div>
        </DialogContent>
      </Dialog>

      {/* Signup Prompt */}
      <Dialog open={showSignupPrompt} onOpenChange={setShowSignupPrompt}>
        <DialogContent data-testid="signup-prompt-modal" className="bg-paper border-gray-400 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Create a <em className="italic">free account</em>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-muted">
              To contact property owners and save favourites, please create a free guest account. It takes less than a minute.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/register')}
                className="btn-editorial btn-solid flex-1"
                data-testid="signup-prompt-register-btn"
              >
                Create Account →
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="btn-editorial btn-ghost border-l"
                style={{ borderLeft: '1px solid rgba(17,17,16,0.85)' }}
              >
                Sign In
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
